import { t } from "@lingui/macro";
import { cn } from "@reactive-resume/utils";
import { useState, useEffect } from "react";

import { YearSelect } from "./year-select";

interface YearDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  className?: string;
}

const PRESENT = "Present";

export const YearDatePicker = ({ value, onChange, className }: YearDatePickerProps) => {
  const [startYear, setStartYear] = useState<string>("");
  const [endYear, setEndYear] = useState<string>(PRESENT);

  // Parse existing value on mount and when value changes
  useEffect(() => {
    if (value) {
      if (value.includes(" - ")) {
        const [start, end] = value.split(" - ");
        setStartYear(start.trim() || "");
        const endValue = end.trim();
        setEndYear(endValue === PRESENT || endValue.toLowerCase() === "present" ? PRESENT : endValue);
      } else if (value === PRESENT || value.toLowerCase() === "present") {
        setStartYear("");
        setEndYear(PRESENT);
      } else {
        // Single year - treat as start year with Present end
        setStartYear(value.trim());
        setEndYear(PRESENT);
      }
    } else {
      setStartYear("");
      setEndYear(PRESENT);
    }
  }, [value]);

  // Generate year options up to the current year only.
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => String(currentYear - i));
  const endYearOptions = [PRESENT, ...years.filter((year) => !startYear || Number(year) >= Number(startYear))];

  const handleStartYearChange = (year: string) => {
    setStartYear(year);
    const nextEndYear =
      endYear !== PRESENT && endYear && Number(endYear) < Number(year) ? year : endYear;

    setEndYear(nextEndYear);
    const endValue = nextEndYear === PRESENT ? PRESENT : nextEndYear;
    onChange(year ? `${year} - ${endValue}` : "");
  };

  const handleEndYearChange = (year: string) => {
    setEndYear(year);
    if (startYear) {
      onChange(`${startYear} - ${year}`);
    } else if (year !== PRESENT) {
      // If no start year but end year selected, set it as start
      setStartYear(year);
      onChange(`${year} - ${PRESENT}`);
    }
  };

  return (
    <div className={cn("flex gap-2 items-center", className)}>
      <YearSelect
        value={startYear}
        onValueChange={handleStartYearChange}
        options={years}
        placeholder="Start Year"
        searchPlaceholder="Search year"
        className="flex-1"
      />

      <span className="text-sm text-secondary-foreground">-</span>

      <YearSelect
        value={endYear}
        onValueChange={handleEndYearChange}
        options={endYearOptions}
        placeholder="End Year"
        searchPlaceholder="Search year"
        className="flex-1"
      />
    </div>
  );
};

